
import { useState, useEffect } from 'react';
import { Lead, LeadStatus } from '@/types/lead';
import { getLeads } from '@/services/leads';
import { getLeadsByUserId } from '@/services/leads/queries/getLeadsByUserId';
import { getStatusDisplayInfo, getLeadStatusOptions } from '@/services/leads/leadStatus';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';

interface DashboardMetrics {
  totalLeads: number;
  newLeads: number;
  favoriteLeads: number;
  avgRating: number;
  leadsByStatus: Array<{ name: string; value: number }>;
  leadsByCategory: Array<{ name: string; value: number }>;
  recentActivities: number;
  pendingTasks: number;
  completedTasks: number;
}

export const useDashboardData = (userId: string | undefined, companyId: string | undefined, showUserLeads: boolean) => {
  const { toast } = useToast();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalLeads: 0,
    newLeads: 0,
    favoriteLeads: 0,
    avgRating: 0,
    leadsByStatus: [],
    leadsByCategory: [],
    recentActivities: 0,
    pendingTasks: 0,
    completedTasks: 0
  });

  const calculateMetrics = async (leadsData: Lead[]) => {
    const totalLeads = leadsData.length;
    const newLeadsCount = leadsData.filter(lead => lead.status === 'new').length;
    const favoriteLeadsCount = leadsData.filter(lead => lead.isFavorite).length;
    
    let totalRating = 0;
    let ratedLeadsCount = 0;
    leadsData.forEach(lead => {
      if (lead.rating) {
        totalRating += lead.rating;
        ratedLeadsCount++;
      }
    });
    const avgRating = ratedLeadsCount > 0 ? (totalRating / ratedLeadsCount) : 0;
    
    const statusOptions = getLeadStatusOptions();
    const statusCounts: Record<string, number> = {};
    
    statusOptions.forEach(option => {
      statusCounts[option.value] = 0;
    });
    
    leadsData.forEach(lead => {
      const status = lead.status || 'new';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });
    
    const leadsByStatus = Object.entries(statusCounts)
      .filter(([_, count]) => count > 0)
      .map(([name, value]) => ({
        name: getStatusDisplayInfo(name as LeadStatus).label,
        value,
        status: name
      }));
    
    const categoryCounts: Record<string, number> = {};
    leadsData.forEach(lead => {
      const category = lead.category || 'Uncategorized';
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });
    
    const leadsByCategory = Object.entries(categoryCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    
    // Optimized: Fetch tasks and activities in parallel queries
    const leadIds = leadsData.map(lead => lead.id);
    
    // Guard: Only fetch tasks/activities if we have leads
    if (leadIds.length === 0) {
      setMetrics({
        totalLeads: 0,
        newLeads: 0,
        favoriteLeads: 0,
        avgRating: 0,
        leadsByStatus: [],
        leadsByCategory: [],
        recentActivities: 0,
        pendingTasks: 0,
        completedTasks: 0
      });
      return;
    }
    
    // Batch queries instead of N+1 per-lead queries
    const [tasksResult, activitiesResult] = await Promise.all([
      supabase.from('tasks').select('status').in('lead_id', leadIds),
      supabase.from('activities').select('created_at').in('lead_id', leadIds)
    ]);

    const tasks = tasksResult.data || [];
    const activities = activitiesResult.data || [];
    
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    
    const pendingTasksCount = tasks.filter(task => task.status !== 'completed').length;
    const completedTasksCount = tasks.filter(task => task.status === 'completed').length;
    const recentActivitiesCount = activities.filter(
      activity => new Date(activity.created_at) > lastWeek
    ).length;
    
    setMetrics({
      totalLeads,
      newLeads: newLeadsCount,
      favoriteLeads: favoriteLeadsCount,
      avgRating,
      leadsByStatus,
      leadsByCategory,
      recentActivities: recentActivitiesCount,
      pendingTasks: pendingTasksCount,
      completedTasks: completedTasksCount
    });
  };

  const fetchLeadsData = async () => {
    if (!userId && showUserLeads) return;
    if (!companyId && !showUserLeads) {
      console.log('Skipping dashboard data fetch: No company ID available');
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const leadsData = showUserLeads 
        ? await getLeadsByUserId(userId!)
        : await getLeads(companyId);
      
      console.log('Fetched leads data:', leadsData);
      setLeads(leadsData);
      await calculateMetrics(leadsData);
    } catch (error) {
      console.error('Error fetching leads data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load dashboard data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeadsData();
  }, [showUserLeads, userId, companyId]);

  return {
    leads,
    isLoading,
    metrics,
    refreshData: fetchLeadsData
  };
};
